import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { IntlContext } from '@/i18n';
import { getCountryCode, getCityName } from '@/modules/common/store/selectors';
import { TLanguageTag } from '@/services/userLanguage';
import { CountryCode, TCountryCodes } from '@/shared/models/country-code';
import { MAIN_HOST, siteDomain } from '@/shared/utils/getSiteDomain';

const checkIOS = () =>
  ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
    navigator.platform,
  ) ||
  // iPad on iOS 13 detection
  (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

const getCountryName = (countryCode: TCountryCodes) => {
  switch (countryCode) {
    case CountryCode.France:
      return 'france';
    case CountryCode.Russia:
      return 'russia';
    case CountryCode.UnitedStates:
      return 'us';
    default:
      return 'uk';
  }
};

const getBookOneDayAppointment = (cityName: string) => {
  switch (cityName) {
    case 'dubai':
      return 'https://cal.com/team/wheely-dubai-ops';
    case 'moscow':
      return 'https://cal.com/team/wheely-moscow/citytest';
    case 'spb':
      return 'https://cal.com/team/wheely-spb';
    case 'sochi':
      return 'https://cal.com/team/wheely-sochi';
    default:
      return 'https://wheely.reservio.com/';
  }
};

const getBookTwoDayAppointment = (cityName: string) => {
  switch (cityName) {
    case 'london':
      return {
        day1: 'https://cal.com/team/wheely-london/city-knowledge-test-london',
        day2: 'https://cal.com/team/wheely-london/city-knowledge-test-london',
      };
    case 'nyc':
      return {
        day1: 'https://cal.com/team/wheely-new-york/english-assessment-city-knowledge',
        day2: 'https://cal.com/team/wheely-new-york/english-assessment-city-knowledge',
      };
  }
};

const getOperationsTeamEmailByCity = (cityName: string) => {
  switch (cityName) {
    case 'london':
      return 'londonpartners@wheely.com';
    case 'paris':
      return 'parispartners@wheely.com';
    case 'dubai':
      return 'dubaipartners@wheely.com';
    case 'moscow':
      return 'moscowpartners@wheely.com';
    case 'spb':
      return 'spbpartners@wheely.com';
    case 'sochi':
      return 'sochipartners@wheely.com';
    default:
      return '';
  }
};

const getOperationsTeamEmailByLanguage = (language: TLanguageTag) => {
  switch (language) {
    case 'fr':
      return 'parispartners@wheely.com';
    case 'ru':
      return 'moscowpartners@wheely.com';
    case 'en':
      return 'nycpartners@wheely.com';
    default:
      return 'londonpartners@wheely.com';
  }
};

const getOperationsTeamEmail = ({
  cityName,
  language,
}: {
  cityName: string;
  language: TLanguageTag;
}) => {
  const email = getOperationsTeamEmailByCity(cityName);

  if (!email) {
    return getOperationsTeamEmailByLanguage(language);
  }

  return email;
};

const getBookInspectionLink = (cityName: string): string => {
  if (cityName === 'paris') {
    return `https://${MAIN_HOST}/fr/faq-for-drivers/france/2424649/4197710`;
  }

  if (cityName === 'moscow') {
    return 'https://cal.com/team/wheely-moscow/vehiclenew';
  }

  // A London chauffeur who is not activated yet gets the car inspected at the City Knowledge Test.
  if (cityName === 'london') {
    return 'https://cal.com/team/wheely-london/city-knowledge-test-london';
  }

  if (cityName === 'nyc') {
    return 'https://cal.com/team/wheely-new-york/vehicle-check-full-team-in-person';
  }

  return getBookOneDayAppointment(cityName);
};

const useExternalLinks = () => {
  const region = useSelector(getCountryCode);
  const cityName = useSelector(getCityName);
  const { language } = useContext(IntlContext);
  const isIOS = checkIOS();
  const countryName = getCountryName(region);

  return useMemo(
    () => ({
      faq: `https://${MAIN_HOST}/${language}/faq-for-drivers/${countryName}`,
      becomingChauffeur: `https://${MAIN_HOST}/en/faq-for-drivers/uk/2424649-partnership/4191334-becoming-a-wheely-chauffeur`,
      faqAcquiringANewVehicle: `https://${MAIN_HOST}/${language}/faq-for-drivers/uk/2424649/8148439`,
      faqUploadingInsurance: `https://${MAIN_HOST}/en/faq-for-drivers/uk/7104480-joining-wheely/16919496-uploading-your-insurance`,
      downloadDriverApp: isIOS
        ? 'https://apps.apple.com/ru/app/wheely-for-chauffeurs/id1473863611'
        : 'https://play.google.com/store/apps/details?id=com.wheely.groundio',
      termsOfUse: `https://${MAIN_HOST}/${language}/legal/${countryName}/for-drivers/terms`,
      cookiePolicy: `https://${MAIN_HOST}/${language}/legal/${countryName}/for-users/cookie`,
      privacyPolicy: `https://${MAIN_HOST}/${language}/legal/${countryName}/for-drivers/privacy`,
      contactOperationTeam: getOperationsTeamEmail({ cityName, language }),
      bookCarInspection: getBookInspectionLink(cityName),
      requirementsForCar: `https://${MAIN_HOST}/${language}/faq-for-drivers/${countryName}/2424649/4191354`,
      bookOneDayAppointment: getBookOneDayAppointment(cityName),
      bookTwoDayAppointment: getBookTwoDayAppointment(cityName),
      bookPreAssessment: 'https://cal.com/team/wheely-france/test-de-pre-evaluation',
      chauffeurLanding: `${siteDomain}/${language}/chauffeur`,
      findInn: 'https://service.nalog.ru/inn.do',
      registerIPorOOO: 'https://www.gosuslugi.ru/situation/kak_otkryt_svoe_delo',
      licensingAuthorities: `https://${MAIN_HOST}/${language}/faq-for-drivers/uk/7104480/6502421`,
      licensingAuthorityTFL: 'https://tfl.gov.uk/info-for/taxis-and-private-hire/licensing',
      licensingAuthorityCastlepoint:
        'https://www.castlepoint.gov.uk/hackney-carriage-and-private-hire-licensing/',
      licensingAuthorityRBWM: `https://${MAIN_HOST}/en/faq-for-drivers/uk/7104480/8607446`,
      licensingAuthorityHertsmere:
        'https://www.hertsmere.gov.uk/business/licensing/private-hire-hackney-carriage-licensing',
      shareFeedback: (() => {
        switch (region) {
          case 'RU':
            return 'https://docs.google.com/forms/d/15TmYETdkLD8q5htiIQ1_ATu5Bzp1T-xFNi8TuDBuOaQ';
          case 'FR':
            return 'https://docs.google.com/forms/d/1RuyVzwnKTLAI863eGaKMCL11yLeuXXB5VUvyvtBvcaE';
          default:
            return 'https://docs.google.com/forms/d/1Y17ugTLSLcfhgJ74HIKAbenZmOt3nLx2_Bl2e080Zzg';
        }
      })(),
    }),
    [isIOS, language, countryName, region, cityName],
  );
};

export { useExternalLinks };
